<template>
  <v-card>
    <v-card-title class="text-h6 pa-5 pb-2">
      {{ modalFormComponent.title }}
    </v-card-title>
    <property-form :value="formValue" :fields="fieldDefs" @input="doSave" />
  </v-card>
</template>

<script>
import PropertyForm from "@/components/PropertyForm";
import { mapGetters } from "vuex";
import { VSelect } from "vuetify/lib/components";

export default {
  name: "CompanyForm",
  components: { PropertyForm },
  props: [],
  computed: {
    ...mapGetters("modals", ["modalFormComponent"]),
    ...mapGetters(["user"]),
    value() {
      return this.modalFormComponent?.value;
    },
    managers() {
      return this.modalFormComponent?.managers;
    },
    formValue() {
      const base = this.value || {
        name: "",
        phone: "",
        inn: "",
        ogrn: "",
        email: "",
        balance: "",
        city: "",
        trial_end_time: "",
        max_courses_trial: "3",
        price_modifier: "1",
      };

      // Конвертируем ISO-дату в формат datetime-local (YYYY-MM-DDTHH:mm)
      let trial_end_time = base.trial_end_time || "";
      if (trial_end_time) {
        try {
          // Если строка уже в формате datetime-local (нет суффикса Z или +) — обрезаем до минут
          if (
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trial_end_time) &&
            !trial_end_time.endsWith("Z") &&
            !trial_end_time.includes("+")
          ) {
            trial_end_time = trial_end_time.slice(0, 16);
          } else {
            // ISO с timezone — переводим в локальное время
            const date = new Date(trial_end_time);
            trial_end_time = new Date(
              date.getTime() - date.getTimezoneOffset() * 60000
            )
              .toISOString()
              .slice(0, 16);
          }
        } catch (e) {
          trial_end_time = "";
        }
      }

      return { ...base, trial_end_time };
    },
    fieldDefs() {
      if (this.user.role === "manager")
        return {
          max_courses_trial: {
            order: 1,
            label: "Максимум назначаемых курсов в пробном периоде",
            visible: true,
            rules: this.patientsRules,
            twoColumns: true,
          },
          price_modifier: {
            order: 2,
            label: "Коэффициент цены",
            visible: true,
            rules: this.priceModifierRules,
            twoColumns: true,
          },
        };

      return {
        name: {
          order: 1,
          label: "Наименование",
          visible: true,
          rules: this.nameRules,
          twoColumns: true,
        },
        email: {
          order: 2,
          label: "E-Mail",
          visible: true,
          rules: this.emailRules,
        },
        phone: {
          order: 3,
          label: "Телефон",
          visible: true,
          rules: this.phoneRules,
        },
        inn: { order: 4, label: "ИНН", visible: true },
        ogrn: { order: 5, label: "ОГРН", visible: true },
        city: {
          order: 6,
          label: "Город",
          visible: true,
          rules: this.cityRules,
        },
        balance: {
          order: 7,
          label: "Баланс",
          visible: true,
          rules: this.balanceRules,
        },
        overdraft: {
          order: 8,
          label: "Овердрафт",
          visible: true,
          rules: this.overdraftRules,
        },
        trial_end_time: {
          order: 9,
          label: "Конец пробного периода",
          type: "datetime-local",
          visible: true,
        },
        max_courses_trial: {
          order: 10,
          label: "Макс. курсов в пробном периоде",
          visible: true,
          rules: this.patientsRules,
        },
        price_modifier: {
          order: 11,
          label: "Коэффициент цены",
          visible: true,
          rules: this.priceModifierRules,
        },
        contract: {
          order: 12,
          label: "Договор",
          visible: true,
        },
        manager_id: {
          order: 13,
          label: "Менеджер",
          visible: true,
          twoColumns: true,
          component: VSelect,
          items: this.managers?.map((m) => ({
            value: m.id,
            text: m.email,
          })),
        },
      };
    },
  },
  data() {
    const modifier = (v) => {
      try {
        return parseFloat(v) >= 1 && /^\d+(\.\d+)?$/.test(v);
      } catch (e) {
        return false;
      }
    };
    return {
      previousBalance: null,
      nameRules: [(v) => !!v || "Обязательное поле"],
      phoneRules: [
        (v) =>
          !v ||
          /^\+\d{1} \(\d{3}\) \d{3} \d{2} \d{2}$/.test(v) ||
          "Пример: +7 (999) 111 22 33",
      ],
      emailRules: [
        (v) => (!!v && !v.includes(" ")) || "Обязательное поле без пробелов",
        (v) => /.+@.+\..+/.test(v) || "Пример: abc@de.fg",
      ],
      balanceRules: [
        (v) => v !== "" || "Значение не может быть пустым",
        (v) => /^[0-9-]*$/.test(v) || "Допустимы только цифры и знак '-'",
        (v) => {
          const overdraft = this.value?.overdraft ?? 0;
          const balance = parseInt(v, 10);
          if (isNaN(balance)) return "Баланс должен быть числом";
          return (
            balance >= -overdraft || `Баланс не может быть меньше ${-overdraft}`
          );
        },
      ],
      overdraftRules: [
        (v) =>
          v === undefined ||
          v === "" ||
          /^-?\d+$/.test(v) ||
          "Допустимы только цифры",
        (v) =>
          v === undefined ||
          v === "" ||
          parseInt(v, 10) >= 0 ||
          "Овердрафт не может быть отрицательным",
      ],
      cityRules: [(v) => !!v || "Обязательное поле"],
      patientsRules: [
        (v) => !!v || /^[0-9]*$/.test(v) || "Значение не может быть меньше 0",
      ],
      priceModifierRules: [
        (v) => !!v || "Обязательно поле",
        (v) => modifier(v) || "Значение не может быть меньше 1",
      ],
    };
  },
  methods: {
    doSave(value) {
      this.$emit("confirm", { ...value });
    },
  },
};
</script>

<style scoped></style>
